import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";

const empty = () => ({
  name: "", description: "", status: "active",
  destination_type: "none", destination_value: "",
  campaign_ids: [], did_ids: [],
});

export default function CustomerInboundGroups() {
  const [rows, setRows] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [dids, setDids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, c, d] = await Promise.all([
        api.get("/customer-routing/inbound-groups", { params: q.trim() ? { q: q.trim() } : {} }),
        api.get("/campaigns"),
        api.get("/customer-routing/dids"),
      ]);
      setRows(g.data || []);
      setCampaigns(c.data || []);
      setDids(d.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty()); setDlg("new"); };
  const openEdit = (r) => {
    setForm({
      name: r.name || "",
      description: r.description || "",
      status: r.status || "active",
      destination_type: r.destination_type || "none",
      destination_value: r.destination_value || "",
      campaign_ids: r.campaign_ids || [],
      did_ids: r.did_ids || [],
    });
    setDlg(r);
  };

  const toggle = (key, id, checked) => {
    setForm((f) => ({
      ...f,
      [key]: checked ? [...f[key], id] : f[key].filter((x) => x !== id),
    }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description || null,
        status: form.status,
        destination_type: form.destination_type,
        destination_value: form.destination_type !== "none" ? (form.destination_value || null) : null,
        campaign_ids: form.campaign_ids,
        did_ids: form.did_ids,
      };
      if (dlg === "new") {
        await api.post("/customer-routing/inbound-groups", body);
        toast.success("Inbound group created");
      } else {
        await api.patch(`/customer-routing/inbound-groups/${dlg.id}`, body);
        toast.success("Inbound group updated");
      }
      setDlg(null); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete inbound group “${r.name}”?`)) return;
    try {
      await api.delete(`/customer-routing/inbound-groups/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  return (
    <div className="space-y-6" data-testid="inbound-groups-page">
      <PageHeader
        title="Inbound Groups"
        subtitle="Route inbound DIDs to campaigns or destinations; attach DIDs to groups"
      />
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="relative">
          <MagnifyingGlass className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8 w-56" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Button className="rounded-lg" onClick={openNew} data-testid="inbound-group-add">
          <Plus className="w-4 h-4 mr-1.5" /> Add group
        </Button>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
        : rows.length === 0 ? <EmptyState title="No inbound groups" description="Create a group, then assign DIDs and campaigns." />
          : (
            <div className="rounded-xl border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>DIDs</TableHead>
                    <TableHead>Campaigns</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <div className="font-medium">{r.name}</div>
                        {r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                      </TableCell>
                      <TableCell className="text-sm">{(r.did_ids || []).length}</TableCell>
                      <TableCell className="text-sm">{(r.campaign_ids || []).length}</TableCell>
                      <TableCell><StatusPill status={r.status} /></TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)}><PencilSimple className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(r)}><Trash className="w-4 h-4 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg" data-testid="inbound-group-dialog">
          <DialogHeader>
            <DialogTitle>{dlg === "new" ? "Add inbound group" : "Edit inbound group"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inbound-group-name" /></div>
            <div className="space-y-1.5"><Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Destination type</Label>
                <Select value={form.destination_type} onValueChange={(v) => setForm({ ...form, destination_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="queue">Queue</SelectItem>
                    <SelectItem value="campaign">Campaign</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.destination_type !== "none" && (
              <div className="space-y-1.5"><Label>Destination value</Label>
                <Input value={form.destination_value} onChange={(e) => setForm({ ...form, destination_value: e.target.value })}
                  placeholder={form.destination_type === "queue" ? "Queue name / ID" : "Campaign reference"} /></div>
            )}
            <div className="space-y-1.5">
              <Label>Attach DIDs</Label>
              <div className="rounded-md border p-2.5 max-h-36 overflow-y-auto space-y-1.5">
                {dids.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.did_ids.includes(d.id)}
                      onCheckedChange={(v) => toggle("did_ids", d.id, !!v)} />
                    <span className="font-mono">{d.number}</span>
                    {d.description && <span className="text-muted-foreground text-xs">{d.description}</span>}
                  </label>
                ))}
                {dids.length === 0 && <p className="text-xs text-muted-foreground">No DIDs yet — add them under DID Management.</p>}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Associate campaigns</Label>
              <div className="rounded-md border p-2.5 max-h-36 overflow-y-auto space-y-1.5">
                {campaigns.filter((c) => !c.archived_at).map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.campaign_ids.includes(c.id)}
                      onCheckedChange={(v) => toggle("campaign_ids", c.id, !!v)} />
                    {c.name}
                    <span className="text-xs text-muted-foreground">{c.dialing_type || "ai_bot"}</span>
                  </label>
                ))}
                {campaigns.length === 0 && <p className="text-xs text-muted-foreground">No campaigns yet.</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setDlg(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={save} disabled={saving} data-testid="inbound-group-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
